import { createEarlyFetchCache } from '@jira/polaris__lib-early-fetch-cache/src/createEarlyFetchCache.tsx';
import { fetchData } from '@jira/polaris__lib-early-fetch-cache/src/fetchData.tsx';
import { waitForGlobalVariable } from '@jira/polaris__lib-early-fetch-cache/src/waitForGlobalVariable.tsx';
import { getEarlyScriptObservabilityCache } from '@jira/polaris__lib-early-fetch-cache/src/getEarlyScriptObservabilityCache.tsx';
import { getProjectIdFromMap } from '@jira/polaris__lib-early-fetch-cache/src/getProjectIdFromMap.tsx';
import { getProjectKeyFromLocation } from '@jira/polaris__lib-early-fetch-cache/src/getProjectKeyFromLocation.tsx';
import { getJpdViewsServicePathPrefix } from '@jira/polaris__lib-jpd-views-service/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { NavigationView } from './NavigationView.types.tsx';

const earlyFetchCache = createEarlyFetchCache<NavigationView[], number>({
	paramsEqualityFn: (paramsA, paramsB) => String(paramsA) === String(paramsB),
});

/**
 * Gets the cached response and marks it as accessed
 * The cache will be automatically cleared after access or if stale
 */
export const getEarlyFetchResponse = (projectId: number): Promise<NavigationView[]> | null => {
	return earlyFetchCache.getCachedResponse(projectId);
};

/**
 * Helper function to fetch project views with proper type
 */
const fetchProjectViews = (projectId: number): Promise<NavigationView[]> => {
	const url = `${getJpdViewsServicePathPrefix()}/v1/navigation/projects/${projectId}/views?operation=getJpdNavigationViews`;
	return fetchData<{
		globalViews?: NavigationView[];
		views: NavigationView[];
	}>({ url, method: 'GET' }).then((response) =>
		fg('polaris_global_views')
			? [
					...(response.globalViews ?? []).map((view) => ({ ...view, global: true as const })),
					...(response.views ?? []),
				]
			: (response.views ?? []),
	);
};

/**
 * Fetches project views data early based on the project id
 */
export const putNavigationProjectViewsEarlyScriptResponse = (projectId: number): void => {
	// Execute the actual fetch for project views
	const response = fetchProjectViews(projectId);

	// Store the fetch promise in the cache
	earlyFetchCache.setCacheResponse(response, projectId);
};

/**
 * Starts the early API request for project views.
 * This function is executed before the main app loads.
 */
export function runGetNavigationProjectViewsEarlyScript(location: Location): boolean {
	// Parse the location to extract the necessary parameters
	const projectKey = getProjectKeyFromLocation(location);
	if (!projectKey) {
		return false;
	}

	// Use cached projectId if available, otherwise wait for SPA_STATE
	getProjectIdFromMap(projectKey, waitForGlobalVariable)
		.then((projectId) => {
			if (projectId) {
				putNavigationProjectViewsEarlyScriptResponse(Number(projectId));
			}
		})
		.catch((error) => {
			getEarlyScriptObservabilityCache()?.recordError(
				'polaris.early-fetch-cache.project-id-mapping',
				'Error fetching project views',
				error instanceof Error ? error : new Error(String(error)),
			);
		});

	return true;
}
